/**
 * Mantenimiento → Sub-Activos (`/mantenimiento/sub-activos`).
 *
 * Puerto de `Maintenance::sub_assets/store_sub_asset/update_sub_asset/
 * toggle_sub_asset_status` + `views/maintenance/sub_assets/index.php`.
 *
 * La tabla del panel anterior mostraba código, nombre, activo padre, tipo y
 * estado; aquí se añaden el **consumo frente al umbral** y el estado calculado
 * (normal, próximo vencimiento o crítico), que es lo que de verdad importa para
 * decidir si toca mantenimiento, y el filtro por activo, categoría y criticidad.
 */

import {
  ArrowLeft,
  Ban,
  Filter,
  Gauge,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  ShieldAlert,
  Wrench,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import {
  useActualizarSubActivo,
  useCambiarEstadoSubActivo,
  useCrearSubActivo,
  useRegistrarMantenimiento,
  useSubActivos,
  useSubActivosCatalogos,
} from '../sub-activos.api';
import { SubActivoDialogo } from '../components/sub-activo-dialogo';
import { SubActivoMantenimiento } from '../components/sub-activo-mantenimiento';
import { varianteEstado, type SubActivo, type SubActivoPayload } from '../sub-activos.types';

/** Filtro de estado del listado. */
type FiltroEstado = 'todos' | 'activos' | 'desuso';

/** Paginación amplia: se filtra en el cliente para que la tabla responda al instante. */
const POR_PAGINA = 200;

/** Color de la barra de consumo según el estado calculado. */
const BARRA: Record<string, string> = {
  danger: 'bg-rose-500',
  warning: 'bg-amber-500',
  success: 'bg-emerald-500',
  neutral: 'bg-ink-300',
};

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

export function SubActivosPage() {
  useDocumentTitle('Mantenimiento · Sub-Activos');

  const [busqueda, setBusqueda] = useState('');
  const [activoId, setActivoId] = useState(0);
  const [categoriaId, setCategoriaId] = useState(0);
  const [estado, setEstado] = useState<FiltroEstado>('todos');
  const [soloCriticos, setSoloCriticos] = useState(false);
  const [dialogo, setDialogo] = useState<{ subActivo: SubActivo | null } | null>(null);
  const [aMantener, setAMantener] = useState<SubActivo | null>(null);
  const [aCambiar, setACambiar] = useState<SubActivo | null>(null);
  const [errorDialogo, setErrorDialogo] = useState<string | null>(null);
  const [errorMantenimiento, setErrorMantenimiento] = useState<string | null>(null);
  const [errorConfirmacion, setErrorConfirmacion] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const subActivos = useSubActivos({ perPage: POR_PAGINA });
  const catalogos = useSubActivosCatalogos();
  const crear = useCrearSubActivo();
  const actualizar = useActualizarSubActivo();
  const cambiarEstado = useCambiarEstadoSubActivo();
  const mantenimiento = useRegistrarMantenimiento();

  const sinPermiso = subActivos.error instanceof ApiError && subActivos.error.status === 403;
  const lista = subActivos.data?.sub_activos ?? [];
  const total = subActivos.data?.total ?? 0;
  const activosPadre = catalogos.data?.activos ?? [];
  const categorias = catalogos.data?.categorias ?? [];

  const termino = busqueda.trim().toLowerCase();
  const filtrados = lista.filter((subActivo) => {
    if (estado === 'activos' && !subActivo.activo_estado) return false;
    if (estado === 'desuso' && subActivo.activo_estado) return false;
    if (activoId && subActivo.activo_id !== activoId) return false;
    if (categoriaId && subActivo.categoria_id !== categoriaId) return false;
    if (soloCriticos && subActivo.estado_mantenimiento !== 'CRITICO') return false;

    if (!termino) return true;

    return (
      subActivo.codigo.toLowerCase().includes(termino) ||
      subActivo.nombre.toLowerCase().includes(termino) ||
      (subActivo.activo ?? '').toLowerCase().includes(termino)
    );
  });

  const criticos = lista.filter(
    (subActivo) => subActivo.estado_mantenimiento === 'CRITICO',
  ).length;
  const enAviso = lista.filter((subActivo) => subActivo.estado_mantenimiento === 'AVISO').length;
  const guardando = crear.isPending || actualizar.isPending;

  /** Guarda el alta o la edición según el diálogo abierto. */
  function guardar(payload: SubActivoPayload) {
    setErrorDialogo(null);

    const comun = {
      onSuccess: (respuesta: { mensaje: string }) => {
        setAviso(respuesta.mensaje);
        setDialogo(null);
      },
      onError: (error: unknown) => setErrorDialogo(mensajeDe(error, 'La API no respondió.')),
    };

    if (dialogo?.subActivo) {
      actualizar.mutate({ id: dialogo.subActivo.id, payload }, comun);
      return;
    }

    crear.mutate(payload, comun);
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/mantenimiento"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Mantenimiento
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Gauge className="size-6 text-brand-500" aria-hidden="true" />
          Sub-activos
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Componentes que se mantienen dentro de un activo. Cada uno lleva su umbral de galonaje: al
          alcanzarlo, el panel avisa de que toca mantenimiento.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {subActivos.data ? (
          <Badge variant={criticos > 0 ? 'danger' : 'outline'}>
            {total} componentes · {criticos} en crítico
          </Badge>
        ) : null}
        <Button
          size="sm"
          leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
          disabled={sinPermiso || activosPadre.length === 0}
          onClick={() => {
            setErrorDialogo(null);
            setDialogo({ subActivo: null });
          }}
        >
          Nuevo sub-activo
        </Button>
      </div>
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>maintenance</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? <Alert variant="success">{aviso}</Alert> : null}

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              containerClassName="min-w-56 flex-1"
              leftIcon={<Search className="size-4" aria-hidden="true" />}
              placeholder="Buscar por código, nombre o activo…"
              value={busqueda}
              onChange={(evento) => setBusqueda(evento.target.value)}
            />

            <Select
              aria-label="Filtrar por activo"
              className={cn(
                'h-12 w-auto min-w-44 rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900',
                'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
              )}
              value={String(activoId)}
              onChange={(evento) => setActivoId(Number(evento.target.value))}
            >
              <option value="0">Todos los activos</option>
              {activosPadre.map((activo) => (
                <option key={activo.id} value={activo.id}>
                  {activo.nombre} · {activo.codigo}
                </option>
              ))}
            </Select>

            <Select
              aria-label="Filtrar por categoría"
              className={cn(
                'h-12 w-auto min-w-40 rounded-control border border-ink-200 bg-white px-4 text-sm text-ink-900',
                'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none',
              )}
              value={String(categoriaId)}
              onChange={(evento) => setCategoriaId(Number(evento.target.value))}
            >
              <option value="0">Todas las categorías</option>
              {categorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nombre}
                </option>
              ))}
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-control border border-ink-200 bg-white p-1">
              <Filter className="mx-1 size-3.5 text-ink-400" aria-hidden="true" />
              {(['todos', 'activos', 'desuso'] as const).map((opcion) => (
                <button
                  key={opcion}
                  type="button"
                  className={cn(
                    'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                    estado === opcion
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-ink-500 hover:bg-ink-50 hover:text-ink-700',
                  )}
                  onClick={() => setEstado(opcion)}
                >
                  {opcion === 'desuso' ? 'en desuso' : opcion}
                </button>
              ))}
            </div>

            <Checkbox
              label="Sólo los que ya pasaron su umbral"
              checked={soloCriticos}
              onChange={(evento) => setSoloCriticos(evento.target.checked)}
            />

            {enAviso > 0 ? (
              <Badge variant="warning">{enAviso} próximos a vencer</Badge>
            ) : null}
          </div>

          {catalogos.data && activosPadre.length === 0 ? (
            <Alert variant="warning" title="Sin activos">
              Antes de registrar sub-activos{' '}
              <Link to="/mantenimiento/activos" className="font-semibold underline">
                crea un activo
              </Link>
              .
            </Alert>
          ) : null}

          {subActivos.isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : subActivos.error ? (
            <Alert variant="error" title="No se pudieron leer los sub-activos">
              {mensajeDe(subActivos.error, 'La API no respondió.')}
            </Alert>
          ) : filtrados.length === 0 ? (
            <Alert variant="info" title="Sin resultados">
              {termino || estado !== 'todos' || activoId || categoriaId || soloCriticos
                ? 'Ningún componente coincide con el filtro.'
                : 'Todavía no hay sub-activos registrados.'}
            </Alert>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Código</th>
                    <th className="py-2">Componente</th>
                    <th className="py-2">Tipo</th>
                    <th className="py-2">Consumo</th>
                    <th className="py-2">Mantenimiento</th>
                    <th className="py-2">Estado</th>
                    <th className="py-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((subActivo) => (
                    <tr key={subActivo.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-3">
                        <span className="font-mono text-xs text-ink-700">{subActivo.codigo}</span>
                      </td>
                      <td className="py-3">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="block font-semibold text-ink-900">
                            {subActivo.nombre}
                          </span>
                          {subActivo.critico ? (
                            <Badge variant="danger">crítico</Badge>
                          ) : null}
                        </span>
                        <span className="block text-[11px] text-ink-400">
                          {subActivo.activo ?? '—'}
                          {subActivo.categoria ? ` · ${subActivo.categoria}` : ''}
                        </span>
                      </td>
                      <td className="py-3 text-ink-600">
                        {subActivo.tipo_componente || <span className="text-ink-300">—</span>}
                      </td>
                      <td className="py-3">
                        <div className="flex min-w-32 flex-col gap-1">
                          <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                            <div
                              className={cn(
                                'h-full rounded-full',
                                BARRA[subActivo.estado_color] ?? 'bg-ink-300',
                              )}
                              style={{ width: `${subActivo.progreso}%` }}
                            />
                          </div>
                          <span className="text-[11px] text-ink-400">
                            {formatNumber(subActivo.usado)} de {formatNumber(subActivo.umbral)} ·{' '}
                            {subActivo.progreso}%
                          </span>
                        </div>
                      </td>
                      <td className="py-3">
                        <Badge variant={varianteEstado(subActivo.estado_color)}>
                          {subActivo.estado_etiqueta}
                        </Badge>
                        {subActivo.proximo_mantenimiento ? (
                          <span className="mt-1 block text-[11px] text-ink-400">
                            Próximo: {subActivo.proximo_mantenimiento}
                          </span>
                        ) : null}
                      </td>
                      <td className="py-3">
                        <Badge variant={subActivo.activo_estado ? 'success' : 'neutral'}>
                          {subActivo.activo_estado ? 'Activo' : 'En desuso'}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Registrar mantenimiento"
                            aria-label={`Registrar mantenimiento de ${subActivo.nombre}`}
                            onClick={() => {
                              setErrorMantenimiento(null);
                              setAMantener(subActivo);
                            }}
                          >
                            <Wrench className="size-4 text-brand-600" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Editar"
                            aria-label={`Editar ${subActivo.nombre}`}
                            onClick={() => {
                              setErrorDialogo(null);
                              setDialogo({ subActivo });
                            }}
                          >
                            <Pencil className="size-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title={subActivo.activo_estado ? 'Marcar en desuso' : 'Reactivar'}
                            aria-label={
                              subActivo.activo_estado
                                ? `Marcar ${subActivo.nombre} en desuso`
                                : `Reactivar ${subActivo.nombre}`
                            }
                            onClick={() => {
                              setErrorConfirmacion(null);
                              setACambiar(subActivo);
                            }}
                          >
                            {subActivo.activo_estado ? (
                              <Ban className="size-4 text-rose-500" aria-hidden="true" />
                            ) : (
                              <RotateCcw className="size-4 text-emerald-600" aria-hidden="true" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {total > lista.length ? (
            <p className="text-xs text-ink-400">
              Mostrando {lista.length} de {total} sub-activos. Usa el buscador para acotar.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {dialogo ? (
        <SubActivoDialogo
          subActivo={dialogo.subActivo}
          activos={activosPadre}
          guardando={guardando}
          error={errorDialogo}
          onGuardar={guardar}
          onCerrar={() => setDialogo(null)}
        />
      ) : null}

      {aMantener ? (
        <SubActivoMantenimiento
          subActivo={aMantener}
          guardando={mantenimiento.isPending}
          error={errorMantenimiento}
          onGuardar={(payload) => {
            setErrorMantenimiento(null);
            mantenimiento.mutate(
              { id: aMantener.id, payload },
              {
                onSuccess: (respuesta) => {
                  setAviso(respuesta.mensaje);
                  setAMantener(null);
                },
                onError: (error) =>
                  setErrorMantenimiento(mensajeDe(error, 'La API no respondió.')),
              },
            );
          }}
          onCerrar={() => setAMantener(null)}
        />
      ) : null}

      {aCambiar ? (
        <ConfirmDialog
          open
          tone={aCambiar.activo_estado ? 'danger' : 'primary'}
          title={aCambiar.activo_estado ? 'Marcar sub-activo en desuso' : 'Reactivar sub-activo'}
          description={
            aCambiar.activo_estado
              ? 'El componente se conserva, pero deja de contar para el mantenimiento.'
              : 'El componente vuelve a contar para el mantenimiento preventivo.'
          }
          confirmLabel={aCambiar.activo_estado ? 'Marcar en desuso' : 'Reactivar'}
          loading={cambiarEstado.isPending}
          onClose={() => setACambiar(null)}
          onConfirm={() => {
            setErrorConfirmacion(null);
            cambiarEstado.mutate(
              { id: aCambiar.id, activo: !aCambiar.activo_estado },
              {
                onSuccess: (respuesta) => {
                  setAviso(respuesta.mensaje);
                  setACambiar(null);
                },
                onError: (error) => setErrorConfirmacion(mensajeDe(error, 'La API no respondió.')),
              },
            );
          }}
        >
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-600">
              <strong className="text-ink-900">{aCambiar.nombre}</strong>{' '}
              <span className="font-mono text-xs">{aCambiar.codigo}</span>
            </p>

            {errorConfirmacion ? (
              <Alert variant="error" title="No se pudo cambiar el estado">
                {errorConfirmacion}
              </Alert>
            ) : null}
          </div>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
